/**
 * The one page shape: finixt-ui's ContentLayout with the admin's measure.
 *
 * Every page is a column of sections in a fixed order, scrolling as a whole,
 * on the canvas. Only the measure varies, and only by what the page holds,
 * expressed as a proportion of the frame: a pixel ceiling has crept back in
 * twice and each time it was the only rule that ever applied, so a display
 * wider than the artboard stopped being used. Below finixt-ui's compact
 * breakpoint the proportion is dropped rather than tightened, since the same
 * share that leaves a comfortable margin on a desktop leaves nothing on a
 * phone.
 *
 * `detail` is the entity page: a back link and a DetailHeader lead the
 * column, and an optional rail docks beside it, outside the scroll.
 */
import type { ReactNode } from 'react';

import { ContentLayout as FinixtContentLayout } from './finixt.js';
import { useMediaQuery } from './useMediaQuery.js';

export type ContentVariant = 'page' | 'reading' | 'form' | 'narrow';

export interface DetailFrame {
  back?: ReactNode;
  header?: ReactNode;
  /** Docked beside the content, full height. */
  rail?: ReactNode;
}

export interface ContentLayoutProps {
  variant?: ContentVariant | undefined;
  detail?: DetailFrame | undefined;
  children: ReactNode;
}

const COMPACT = '(max-width: 640px)';

/** Measures, as proportions of the frame. */
const MEASURE: Record<ContentVariant, string> = {
  page: '100%',
  reading: '90%',
  form: '80%',
  narrow: '70%',
};

export function ContentLayout({ variant = 'page', detail, children }: ContentLayoutProps) {
  const compact = useMediaQuery(COMPACT);
  const maxWidth = compact ? '100%' : MEASURE[detail ? 'reading' : variant];
  return (
    <FinixtContentLayout maxWidth={maxWidth} {...(detail?.rail ? { rail: detail.rail } : {})}>
      {detail?.back}
      {detail?.header}
      {children}
    </FinixtContentLayout>
  );
}
